/**
 * Column Triggers API — typed wrappers over the gateway endpoints
 * defined in apps/chaos-gateway/src/column-triggers.ts
 * (Milestone 2.4, spec §3.13).
 *
 * Endpoints:
 *   GET    /api/columns/:columnId/triggers
 *   POST   /api/columns/:columnId/triggers
 *   PATCH  /api/triggers/:triggerId
 *   DELETE /api/triggers/:triggerId
 *
 * The body for create/update is validated client-side via the Zod
 * discriminated union in @chaos/types/column-triggers, then validated
 * again on the server before insert.
 */
import type {
  ColumnTrigger,
  CreateColumnTriggerInput,
  UpdateColumnTriggerInput,
} from '@chaos/types/column-triggers';

import { apiJson } from './client';

interface TriggersListResponse {
  triggers: ColumnTrigger[];
}
interface TriggerResponse {
  trigger: ColumnTrigger;
}

export const columnTriggersApi = {
  list(columnId: string): Promise<ColumnTrigger[]> {
    return apiJson<TriggersListResponse>(`/columns/${columnId}/triggers`).then((r) => r.triggers);
  },
  create(columnId: string, input: CreateColumnTriggerInput): Promise<ColumnTrigger> {
    return apiJson<TriggerResponse>(`/columns/${columnId}/triggers`, {
      method: 'POST',
      json: input,
    }).then((r) => r.trigger);
  },
  update(triggerId: string, input: UpdateColumnTriggerInput): Promise<ColumnTrigger> {
    return apiJson<TriggerResponse>(`/triggers/${triggerId}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.trigger);
  },
  remove(triggerId: string): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(`/triggers/${triggerId}`, { method: 'DELETE' });
  },
};

export const columnTriggersKeys = {
  all: ['column-triggers'] as const,
  byColumn: (columnId: string) => [...columnTriggersKeys.all, 'byColumn', columnId] as const,
};
